import {
    alpha,
    Box,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { motion, useScroll, useTransform, Variants } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import ApplicationButton from "../../../../Components/ApplicationButton";
import { getLocalizationFromDescriptor } from "../../../../Helpers/TextDescriptorUtils";
import { getOrDefault } from "../../../../Helpers/Utils";
import { useMicrospecThemeLanguageContext } from "../../../../Themes";
import { HeroSectionWithScrollTriggerProps } from "./types";

const titlesVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            duration: 2,
            staggerChildren: 0.2,
        },
    },
};

const HeroSectionWithScrollTrigger: React.FC<HeroSectionWithScrollTriggerProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const randomKey = Math.random().toString(36).substring(2, 15);
    const [scrollOffset, setScrollOffset] = useState<number>(0);
    const imageRef = useRef<HTMLDivElement>(null);

    const heroImageSource = getOrDefault(spec.heroImageSource, null);
    const heroVideoSource = getOrDefault(spec.heroVideoSource, null);

    // Ensure mutual exclusivity - heroVideoSource takes precedence
    const effectiveHeroImageSource = heroVideoSource ? null : heroImageSource;
    const effectiveHeroVideoSource = heroVideoSource;

    // Parallax scroll effect for hero image/video (only when hero media is provided)
    const hasHeroMedia = effectiveHeroImageSource || effectiveHeroVideoSource;
    const { scrollYProgress } = useScroll({
        target: hasHeroMedia ? imageRef : undefined,
        offset: ["start end", "end start"]
    });
    const imageY = useTransform(scrollYProgress, [0, 1], [100, -100]);
    const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.9]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTarget = document.getElementById(randomKey);
            if (scrollTarget) {
                const rect = scrollTarget.getBoundingClientRect();
                const sectionTopPosition = window.scrollY + rect.top;

                // Calculate scroll offset only after section reaches top of screen
                const scrollBeyondTop = window.scrollY - sectionTopPosition;
                setScrollOffset(Math.max(0, scrollBeyondTop));
            }
        };

        // Initial calculation
        handleScroll();

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [randomKey]);

    const linesDistanceFromScreen = 8;

    const backgroundImageSource = getOrDefault(spec.backgroundImageSource, "")
    const backgroundVideoSource = getOrDefault(spec.backgroundVideoSource, "")
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.default)
    const backgroundFilter = getOrDefault(spec.backgroundFilter, null)
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main)

    // Derive contrast colors based on background
    const hasBackgroundMedia = backgroundImageSource || backgroundVideoSource;
    const defaultTextColor = hasBackgroundMedia
        ? theme.palette.common.white
        : theme.palette.text.primary;
    const defaultDividerColor = hasBackgroundMedia
        ? theme.palette.common.white
        : alpha(theme.palette.text.primary, 0.3);

    const dividerColor = defaultDividerColor;

    const topComponent = getOrDefault(spec.topComponent, null)
    const mainTitleTextObject = getLocalizationFromDescriptor(spec.mainTitleText)
    const mainTitleText = localizationProvider.translateByType(mainTitleTextObject);
    const mainTitleColor = getOrDefault(spec.mainTitleColor, defaultTextColor)

    const secondaryTitleTextObject = getLocalizationFromDescriptor(spec.secondaryTitleText)
    const secondaryTitleText = localizationProvider.translateByType(secondaryTitleTextObject);
    const secondaryTitleColor = getOrDefault(spec.secondaryTitleColor, defaultTextColor)

    const actionButtonTextObject = getLocalizationFromDescriptor(spec.actionButtonText);
    const actionButtonText = localizationProvider.translateByType(actionButtonTextObject);
    const actionButtonTextColor = getOrDefault(spec.actionButtonTextColor, theme.palette.primary.contrastText)
    const actionButtonBackground = getOrDefault(spec.actionButtonBackground, theme.palette.primary.main)
    const actionButtonHref = getOrDefault(spec.actionButtonHref, "#")

    const secondaryActionButtonTextObject = getLocalizationFromDescriptor(spec.secondaryActionButtonText);
    const secondaryActionButtonText = localizationProvider.translateByType(secondaryActionButtonTextObject);
    const secondaryActionButtonTextColor = getOrDefault(spec.secondaryActionButtonTextColor, defaultTextColor)
    const secondaryActionButtonBackground = getOrDefault(spec.secondaryActionButtonBackground, 'transparent')
    const secondaryActionButtonHref = getOrDefault(spec.secondaryActionButtonHref, "#")

    const showBottomText = getOrDefault(spec.showBottomText, true)
    const bottomTextObject = getLocalizationFromDescriptor(spec.bottomText)
    const bottomText = localizationProvider.translateByType(bottomTextObject);
    const bottomTextColor = getOrDefault(spec.bottomTextColor, alpha(defaultTextColor, 0.7))


    return (
        <Paper
            id={spec.id}
            sx={{
                overflowX: "unset",
                maxWidth: "100vw",
                display: "block",
            }}
        >
            <Box>
                <Box
                    sx={{
                        position: "relative",
                        height: "100vh !important",
                        overflow: "hidden",
                        right: 0,
                        top: 0,
                        background: !hasBackgroundMedia ? backgroundColor : undefined,
                    }}
                >
                    {/* Animated background orbs (shown only if no image/video) */}
                    {!hasBackgroundMedia && (
                        <>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '10%',
                                    left: '-5%',
                                    width: 600,
                                    height: 600,
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.15)}, transparent)`,
                                    filter: 'blur(80px)',
                                    animation: 'float-slow 25s ease-in-out infinite',
                                    '@keyframes float-slow': {
                                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                                        '50%': { transform: 'translate(100px, 100px) scale(1.1)' }
                                    }
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: '10%',
                                    right: '-5%',
                                    width: 500,
                                    height: 500,
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.12)}, transparent)`,
                                    filter: 'blur(70px)',
                                    animation: 'float-reverse-slow 20s ease-in-out infinite',
                                    '@keyframes float-reverse-slow': {
                                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                                        '50%': { transform: 'translate(-80px, -80px) scale(1.15)' }
                                    }
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '20%',
                                    width: 400,
                                    height: 400,
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.1)}, transparent)`,
                                    filter: 'blur(60px)',
                                    animation: 'float-diagonal 22s ease-in-out infinite',
                                    '@keyframes float-diagonal': {
                                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                                        '50%': { transform: 'translate(-60px, 60px) scale(1.08)' }
                                    }
                                }}
                            />
                        </>
                    )}

                    {/* Background video with scroll effects */}
                    {backgroundVideoSource && (
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{
                                width: "100%",
                                display: "block",
                                height: "100%",
                                objectFit: "cover",
                                filter: scrollOffset > 0
                                    ? `blur(${Math.min(scrollOffset * 0.1, 30)}px)`
                                    : undefined,
                                transform: scrollOffset > 0
                                    ? `scale(${Math.min(1 + scrollOffset * 0.001, 1.3)})`
                                    : undefined,
                                transition: "all 0.3s ease-out",
                            }}
                        >
                            <source src={backgroundVideoSource} type="video/mp4" />
                        </video>
                    )}

                    {/* Background image with scroll effects */}
                    {backgroundImageSource && !backgroundVideoSource && (
                        <img
                            alt="background with zooming effect"
                            style={{
                                width: "100%",
                                display: "block",
                                height: "100%",
                                objectFit: "cover",
                                filter: scrollOffset > 0
                                    ? `blur(${Math.min(scrollOffset * 0.1, 30)}px)`
                                    : undefined,
                                transform: scrollOffset > 0
                                    ? `scale(${Math.min(1 + scrollOffset * 0.001, 1.3)})`
                                    : undefined,
                                transition: "all 0.3s ease-out",
                            }}
                            src={backgroundImageSource}
                        />
                    )}

                    {/* Background filter overlay */}
                    {backgroundFilter && (backgroundImageSource || backgroundVideoSource) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: backgroundFilter.type === 'darken' 
                                    ? `rgba(0, 0, 0, ${backgroundFilter.intensity})`
                                    : `rgba(255, 255, 255, ${backgroundFilter.intensity})`,
                                zIndex: 1,
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                    <Stack
                        sx={{
                            height: "100%",
                            width: "100%",
                            position: "absolute",
                            zIndex: 10,
                            top: 0,
                            right: 0,
                            left: 0,
                            justifyContent: "center",
                            alignItems: "flex-start",
                        }}
                    >
                        <Stack
                            sx={{
                                width: "100%",
                                height: "100%",
                                justifyContent: "center",
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: "center",
                                flexWrap: "wrap",
                                px: { xs: 2, sm: linesDistanceFromScreen + 3, md: linesDistanceFromScreen + 16 },
                                zIndex: 2,
                            }}
                            gap={2}
                        >
                            <motion.div
                                variants={titlesVariants}
                                initial="hidden"
                                animate="show"
                                viewport={{ once: true }}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flex: 1,
                                    width: "100%",
                                    maxWidth: "100%",
                                }}
                            >
                                {topComponent && <Box>{topComponent}</Box>}
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontSize: { xs: '1.75rem', sm: '2.8rem', md: '3.5rem', lg: '4rem' },
                                        fontWeight: 800,
                                        color: secondaryTitleColor,
                                        textAlign: "center",
                                        maxWidth: { xs: '100%', sm: '620px' },
                                        width: '100%',
                                        mb: 2,
                                        lineHeight: { xs: 1.2, md: 1.2 },
                                        textShadow: hasBackgroundMedia ? '0 2px 10px rgba(0,0,0,0.3)' : 'none',
                                        px: { xs: 1, sm: 0 },
                                    }}

                                >
                                    {mainTitleText}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1.1rem', sm: '1.4rem', md: '1.6rem' },
                                        fontWeight: 400,
                                        color: alpha(mainTitleColor, 0.9),
                                        textAlign: "center",
                                        mb: 2,
                                        lineHeight: 1.5,
                                        px: { xs: 1, sm: 0 },
                                        maxWidth: { xs: '100%', sm: '70%' },
                                    }}
                                >
                                    {secondaryTitleText}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        maxWidth: { xs: '100%', sm: 600 }
                                    }}
                                >
                                    <ApplicationButton
                                        href={actionButtonHref}
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            background: `linear-gradient(45deg, ${actionButtonBackground}, ${alpha(actionButtonBackground, 0.8)})`,
                                            color: actionButtonTextColor,
                                            px: { xs: 3, sm: 4 },
                                            py: { xs: 1.2, sm: 1.5 },
                                            fontSize: { xs: '1rem', sm: '1.1rem' },
                                            fontWeight: 600,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            flex: { xs: '0 0 auto', sm: 1 },
                                            maxWidth: { xs: '100%', sm: 280 },
                                            boxShadow: `0 8px 25px ${alpha(actionButtonBackground, 0.4)}`,
                                            transition: 'all 0.3s ease',
                                            minHeight: { xs: '48px', sm: '56px' },
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: '-100%',
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                transition: 'left 0.5s ease'
                                            },
                                            "&:hover": {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 12px 35px ${alpha(actionButtonBackground, 0.6)}`,
                                                '&::before': {
                                                    left: '100%'
                                                }
                                            },
                                            "&:active": {
                                                transform: 'translateY(0)',
                                            },
                                        }}
                                    >
                                        {actionButtonText}
                                    </ApplicationButton>
                                    {secondaryActionButtonText && (
                                        <ApplicationButton
                                            href={secondaryActionButtonHref}
                                            variant="outlined"
                                            size="large"
                                            sx={{
                                                color: secondaryActionButtonTextColor,
                                                borderColor: secondaryActionButtonTextColor,
                                                backgroundColor: secondaryActionButtonBackground,
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.2, sm: 1.5 },
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                fontWeight: 600,
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                flex: { xs: '0 0 auto', sm: 1 },
                                                maxWidth: { xs: '100%', sm: 280 },
                                                borderWidth: 2,
                                                transition: 'all 0.3s ease',
                                                minHeight: { xs: '48px', sm: '56px' },
                                                backdropFilter: 'blur(10px)',
                                                "&:hover": {
                                                    transform: 'translateY(-2px)',
                                                    borderWidth: 2,
                                                    backgroundColor: alpha(secondaryActionButtonTextColor, 0.1),
                                                    boxShadow: `0 8px 25px ${alpha(secondaryActionButtonTextColor, 0.3)}`,
                                                },
                                                "&:active": {
                                                    transform: 'translateY(0)',
                                                },
                                            }}
                                        >
                                            {secondaryActionButtonText}
                                        </ApplicationButton>
                                    )}
                                </Box>
                            </motion.div>
                            {hasHeroMedia && (
                                <Box
                                    sx={{
                                        width: { xs: '100%', sm: '40%' },
                                        // maxWidth: { xs: '100%', sm: 450 },
                                        zIndex: 1,
                                        mt: { xs: 4, sm: 0 },
                                        mb: { xs: 3, sm: 0 },
                                        display: { xs: 'none', md: 'block' }
                                    }}
                                >
                                    <motion.div
                                        ref={imageRef}
                                        initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
                                        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                                        transition={{ duration: 1, ease: "easeOut" }}
                                    >
                                        <motion.div
                                            style={{
                                                y: isMobile ? 0 : imageY,
                                                scale: imageScale
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    borderRadius: 4,
                                                    overflow: 'hidden',
                                                    height: { xs: 280, sm: 350, md: 450 },
                                                    boxShadow: `0 24px 80px ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.3)}`,
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: `linear-gradient(135deg, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.15)} 0%, transparent 100%)`,
                                                        zIndex: 2,
                                                        pointerEvents: 'none'
                                                    },
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        inset: 0,
                                                        border: `2px solid ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.3)}`,
                                                        borderRadius: 4,
                                                        zIndex: 3,
                                                        pointerEvents: 'none'
                                                    }
                                                }}
                                            >
                                                {effectiveHeroVideoSource ? (
                                                    <Box
                                                        component="video"
                                                        autoPlay
                                                        loop
                                                        muted
                                                        playsInline
                                                        onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            position: 'relative',
                                                            zIndex: 1,
                                                            transition: 'transform 0.6s ease',
                                                            '&:hover': {
                                                                transform: 'scale(1.05)'
                                                            }
                                                        }}
                                                    >
                                                        <source src={effectiveHeroVideoSource} type="video/mp4" />
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        component="img"
                                                        src={effectiveHeroImageSource}
                                                        alt="Hero"
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            position: 'relative',
                                                            zIndex: 1,
                                                            transition: 'transform 0.6s ease',
                                                            '&:hover': {
                                                                transform: 'scale(1.05)'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </motion.div>
                                    </motion.div>
                                </Box>
                            )}

                        </Stack>

                        {/* Hero Image with Parallax */}

                        <Box
                            key="vertical-lines-box"
                            sx={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0,0,0,0)",
                                position: "absolute",
                                zIndex: 0,
                                mx: "auto",
                                display: { xs: 'none', sm: 'block' },
                                "&:before": {
                                    content: '""',
                                    width: "100%",
                                    height: "100%",
                                    borderLeft: `1px dashed ${dividerColor}`,
                                    position: "absolute",
                                    left: linesDistanceFromScreen * 8,
                                },
                                "&:after": {
                                    content: '""',
                                    width: "1px",
                                    height: "100%",
                                    borderRight: `1px dashed ${dividerColor}`,
                                    position: "absolute",
                                    right: linesDistanceFromScreen * 8,
                                },
                            }}
                        ></Box>
                        {showBottomText && <Box
                            key="horizontal-lines-box"
                            sx={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0,0,0,0)",
                                position: "absolute",
                                zIndex: 1,
                                mx: "auto",
                                "&:before": {
                                    content: '""',
                                    width: "100%",
                                    height: "1px",
                                    borderBottom: `1px dashed ${dividerColor}`,
                                    position: "absolute",
                                    bottom: "50%",
                                    left: `calc(100% - ${linesDistanceFromScreen * 8}px)`,
                                    display: { xs: 'none', sm: 'block' },
                                },
                                "&:after": {
                                    content: '""',
                                    height: "1px",
                                    width: "100%",
                                    borderTop: `1px dashed ${dividerColor}`,
                                    position: "absolute",
                                    bottom: "50%",
                                    right: `calc(100% - ${linesDistanceFromScreen * 8}px)`,
                                    display: { xs: 'none', sm: 'block' },
                                },
                            }}
                        >
                            <Stack
                                sx={{
                                    width: "100%",
                                    height: 110,
                                    position: "absolute",
                                    bottom: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ color: bottomTextColor }}>{bottomText}</Typography>
                                <Stack
                                    sx={{
                                        width: 64,
                                        display: "flex",
                                        flex: 1,
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "fit-content",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            border: `2.5px dashed ${dividerColor}`,
                                            transform: "translateY(16px)",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            height: "calc(100% - 32px)",
                                            width: 2,
                                            borderRight: `1px solid ${dividerColor}`,
                                            position: "relative",
                                            "&:before": {
                                                content: '""',
                                                height: linesDistanceFromScreen * 0.3,
                                                width: linesDistanceFromScreen * 0.3,
                                                backgroundColor: dividerColor,
                                                borderRadius: "50%",
                                                position: "absolute",
                                                top: 0,
                                                left: `calc(50% - ${linesDistanceFromScreen * 0.05}px)`,
                                            },
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                        }
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default HeroSectionWithScrollTrigger;
